import { useEffect, useState } from "react";
import { Search, User, Phone, MapPin, Clock, CheckCircle, AlertCircle, Users } from "lucide-react";
import { publicApi, api } from "../api/client";
import type { Recipient } from "../types";

export default function PublicCheck() {
  const [query, setQuery] = useState("");
  const [jurusan, setJurusan] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  
  // WAKIL state
  const [selectedType, setSelectedType] = useState<"SENDIRI" | "WAKIL" | null>(null);
  const [wakilForm, setWakilForm] = useState({
    nama: "",
    noIc: "",
    noTel: "",
    alamat: "",
    hubungan: "",
  });
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState("");
  const [successNotice, setSuccessNotice] = useState<{ title: string; message: string } | null>(null);
  const [submittedUpvoted, setSubmittedUpvoted] = useState<{
    nama: string;
    noIc: string;
    noTel: string;
    alamat: string;
    hubungan: string;
  } | null>(null);

  useEffect(() => {
    document.title = "Daftar Kehadiran Insentif Mahasiswa Anak Johor 2026 (IMAJ)";
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError("");
    setRecipient(null);
    setCheckInMessage("");
    setSelectedType(null);
    setSubmittedUpvoted(null);

    try {
      const result = await publicApi.searchRecipient(query.trim());
      if (result.error) {
        setError(result.error);
      } else if (result.recipient) {
        setRecipient(result.recipient);
      }
    } catch (err) {
      setError("Ralat berlaku. Sila cuba lagi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setQuery("");
    setJurusan("");
    setRecipient(null);
    setError("");
    setSelectedType(null);
    setWakilForm({ nama: "", noIc: "", noTel: "", alamat: "", hubungan: "" });
    setCheckInMessage("");
    setSuccessNotice(null);
    setSubmittedUpvoted(null);
  };

  const handleCheckInSendiri = async () => {
    if (!recipient || recipient.statusKehadiran === 1) return;

    setIsCheckingIn(true);
    setCheckInMessage("");
    try {
      const result = await api.checkIn(recipient.id, undefined, { type: "SENDIRI", jurusan: jurusan.trim() });
      if (result.success) {
        setCheckInMessage("");
        setSuccessNotice({
          title: "Berjaya",
          message: "Anda telah mendaftar sebagai SENDIRI.",
        });
        setRecipient({ ...recipient, statusKehadiran: 1 });
      } else {
        setCheckInMessage(result.message || "Ralat berlaku");
      }
    } catch (err: any) {
      setCheckInMessage(err.message || "Ralat berlaku");
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckInWakil = async () => {
    if (!recipient || recipient.statusKehadiran === 1) return;
    
    // Validate WAKIL form
    if (!wakilForm.nama || !wakilForm.noIc || !wakilForm.hubungan) {
      setCheckInMessage("Sila lengkapkan maklumat WAKIL");
      return;
    }

    setIsCheckingIn(true);
    setCheckInMessage("");
    try {
      const result = await api.checkIn(recipient.id, undefined, {
        type: "WAKIL",
        jurusan: jurusan.trim(),
        wakilNama: wakilForm.nama,
        wakilNoIc: wakilForm.noIc,
        wakilNoTel: wakilForm.noTel,
        wakilAlamat: wakilForm.alamat,
        wakilHubungan: wakilForm.hubungan,
      });
      if (result.success) {
        setCheckInMessage("");
        setSuccessNotice({
          title: "Berjaya",
          message: "Anda telah mendaftar sebagai WAKIL.",
        });
        setSubmittedUpvoted({ ...wakilForm });
        setRecipient({ ...recipient, statusKehadiran: 1, ...result.recipient });
      } else {
        setCheckInMessage(result.message || "Ralat berlaku");
      }
    } catch (err: any) {
      setCheckInMessage(err.message || "Ralat berlaku");
    } finally {
      setIsCheckingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">B</span>
              </div>
              <div>
                <h1 className="font-semibold text-slate-800">Daftar Kehadiran Semakan Insentif Mahasiswa Anak Johor 2026 (IMAJ)</h1>
                <p className="text-xs text-slate-500">Peringkat Dun Permas dan Dun Johor Jaya</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Image */}
      <div className="flex justify-center bg-white border-b border-slate-200 px-4 py-4">
        <img
          src="/banner.jpg"
          alt="Majlis Insentif Mahasiswa Anak Johor (IMAJ)"
          className="w-full max-w-sm rounded-xl object-contain"
        />
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Masukkan No. IC"
                className="w-full px-4 py-3 text-lg border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !query.trim()}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isLoading ? "Mencari..." : "Cari"}
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-600 mt-2">Mencari...</p>
          </div>
        )}

        {/* Result */}
        {recipient && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Status Banner */}
            <div className={`px-6 py-4 ${recipient.statusKehadiran === 1 ? "bg-green-50" : "bg-red-50"}`}>
              <div className="flex items-center gap-3">
                {recipient.statusKehadiran === 1 ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">Sudah Daftar Masuk</p>
                      <p className="text-sm text-green-600">
                        {recipient.checkedInAt && new Date(recipient.checkedInAt).toLocaleString("ms-MY", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Clock className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-semibold text-red-800">Belum Hadir</p>
                      <p className="text-sm text-red-600">Sila tekan butang dibawah untuk pengesahan kehadiran</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* WAKIL Details Confirmation - Show after successful WAKIL check-in or if already checked in as WAKIL */}
            {(submittedUpvoted || (recipient.statusKehadiran === 1 && (recipient as any).wakilNama)) && (
              <div className="bg-amber-50 border-t border-amber-200 px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-800">Maklumat WAKIL / Representative</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-amber-600">Nama</p>
                    <p className="font-medium text-amber-900">{submittedUpvoted?.nama || (recipient as any).wakilNama}</p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-600">No. IC</p>
                    <p className="font-medium text-amber-900 font-mono">{submittedUpvoted?.noIc || (recipient as any).wakilNoIc}</p>
                  </div>
                  {(submittedUpvoted?.noTel || (recipient as any).wakilNoTel) && (
                    <div>
                      <p className="text-xs text-amber-600">No. Telefon</p>
                      <p className="font-medium text-amber-900">{submittedUpvoted?.noTel || (recipient as any).wakilNoTel}</p>
                    </div>
                  )}
                  {(submittedUpvoted?.alamat || (recipient as any).wakilAlamat) && (
                    <div>
                      <p className="text-xs text-amber-600">Alamat</p>
                      <p className="font-medium text-amber-900">{submittedUpvoted?.alamat || (recipient as any).wakilAlamat}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-amber-600">Hubungan</p>
                    <p className="font-medium text-amber-900">{submittedUpvoted?.hubungan || (recipient as any).wakilHubungan}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <p className="text-xs text-amber-700">
                    ✓️ Pendaftaran berjaya. Sila tunjukkan resit ini kepada urusetia.
                  </p>
                </div>
              </div>
            )}

            {/* Details */}
            <div className="p-6">
              {/* Top Section - Bil, Slot Masa, DUN, Umur, Institusi */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Bil (Rujukan)</p>
                    <p className="text-3xl md:text-4xl font-extrabold text-blue-700 font-mono tracking-wide leading-none">
                      {recipient.bil}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Slot Masa</p>
                    <p className="text-lg font-semibold text-slate-700">{recipient.slotMasa || "-"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">DUN / Kawasan</p>
                    <p className="text-lg font-semibold text-slate-700">{recipient.dun}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Umur</p>
                    <p className="text-lg font-semibold text-slate-700">{recipient.age ?? "-"}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Institusi / Universiti</p>
                  <p className="text-xl font-bold text-blue-600 break-words">
                    {recipient.institution || "-"}
                  </p>
                </div>
              </div>

              {/* Personal Details */}
              <div className="grid gap-4">
                <DetailRow label="Nama" value={recipient.nama} icon={<User className="w-4 h-4" />} />
                <DetailRow label="No. IC" value={recipient.noIc} icon={<span className="font-mono">IC</span>} />
                {recipient.noTelefon && (
                  <DetailRow label="No. Telefon" value={recipient.noTelefon} icon={<Phone className="w-4 h-4" />} />
                )}
                {recipient.alamat && (
                  <DetailRow label="Alamat" value={recipient.alamat} icon={<MapPin className="w-4 h-4" />} />
                )}
              </div>

              {/* Jurusan Field - Required before check-in */}
              <div className="bg-slate-100 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Bidang Pengajian / Jurusan *
                </label>
                <input
                  type="text"
                  value={jurusan}
                  onChange={(e) => setJurusan(e.target.value.toUpperCase())}
                  placeholder="MASUKKAN JURUSAN / FIELD OF STUDIES"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase"
                />
              </div>

              {/* Action Buttons - Only show if not yet checked in */}
              {recipient.statusKehadiran === 0 && (
                <div className="border-t border-slate-200 p-6 bg-slate-50">
                  {checkInMessage && (
                    <div className={`p-3 rounded-lg mb-4 text-center font-semibold ${checkInMessage.includes("✓") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {checkInMessage}
                    </div>
                  )}
                  
                  {!selectedType ? (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={handleCheckInSendiri}
                        disabled={isCheckingIn || !jurusan.trim()}
                        className="flex-1 py-4 bg-green-600 text-white text-xl font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                      >
                        <User className="w-6 h-6" />
                        SENDIRI
                      </button>
                      <button
                        onClick={() => setSelectedType("WAKIL")}
                        disabled={isCheckingIn || !jurusan.trim()}
                        className="flex-1 py-4 bg-amber-500 text-white text-xl font-semibold rounded-xl hover:bg-amber-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
                      >
                        <Users className="w-6 h-6" />
                        WAKIL
                      </button>
                    </div>
                  ) : selectedType === "WAKIL" && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-300">
                      <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Maklumat WAKIL / Representative
                      </h3>
                      <div className="grid gap-3">
                        <div>
                          <label className="block text-sm text-amber-700 mb-1">Nama Penuh WAKIL *</label>
                          <input
                            type="text"
                            value={wakilForm.nama}
                            onChange={(e) => setWakilForm({ ...wakilForm, nama: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                            placeholder="Nama penuh"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm text-amber-700 mb-1">No. IC WAKIL *</label>
                            <input
                              type="text"
                              value={wakilForm.noIc}
                              onChange={(e) => setWakilForm({ ...wakilForm, noIc: e.target.value })}
                              className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                              placeholder="No. IC"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-amber-700 mb-1">No. Telefon</label>
                            <input
                              type="text"
                              value={wakilForm.noTel}
                              onChange={(e) => setWakilForm({ ...wakilForm, noTel: e.target.value })}
                              className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                              placeholder="No. Telefon"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-amber-700 mb-1">Alamat</label>
                          <input
                            type="text"
                            value={wakilForm.alamat}
                            onChange={(e) => setWakilForm({ ...wakilForm, alamat: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                            placeholder="Alamat"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-amber-700 mb-1">Hubungan dengan Penerima *</label>
                          <select
                            value={wakilForm.hubungan}
                            onChange={(e) => setWakilForm({ ...wakilForm, hubungan: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                          >
                            <option value="">Pilih Hubungan</option>
                            <option value="SUAMI">Suami / Isteri</option>
                            <option value="ANAK">Anak</option>
                            <option value="ADIK">Adik Beradik</option>
                            <option value="IBUBAPA">Ibubapa</option>
                            <option value="MENANTU">Menantu</option>
                            <option value="SAUDARA">Saudara</option>
                            <option value="KAWAN">Kawan / Jiran</option>
                            <option value="LAIN">Lain-lain</option>
                          </select>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 mt-2">
                          <button
                            onClick={() => setSelectedType(null)}
                            className="flex-1 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition"
                          >
                            Batal
                          </button>
                          <button
                            onClick={handleCheckInWakil}
                            disabled={isCheckingIn || !wakilForm.nama || !wakilForm.noIc || !wakilForm.hubungan}
                            className="flex-1 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 disabled:opacity-50 transition"
                          >
                            {isCheckingIn ? "Memproses..." : "Hantar"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && !recipient && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Masukkan No. IC untuk semakan</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-slate-400">
        <p>© 2026 Portal Bantuan Mahasiswa DUN Permas & Johor Jaya</p>
      </footer>

      {successNotice && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSuccessNotice(null)}
        >
          <div className="w-full max-w-md bg-white rounded-2xl p-6 border border-green-200 shadow-xl text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-700 mb-2">{successNotice.title}</p>
            <p className="text-slate-700">{successNotice.message}</p>
            <p className="text-sm text-slate-500 mt-4">Sentuh skrin untuk tutup.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-medium text-slate-800 break-words whitespace-normal leading-snug">{value}</p>
      </div>
    </div>
  );
}
